/* Casani 2026 homepage behaviour. Loaded once from the header so it also covers
   sections the theme lazy-loads (inline scripts in those sections never run). */
(function () {
  // Open Shopify Inbox from any [data-cz-chat] link; fall back to the link's href.
  function findChatButton(root) {
    if (!root) return null;
    const direct = root.querySelector('button.activator, button[aria-label*="Open chat"]');
    if (direct) return direct;
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        const found = findChatButton(el.shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cz-chat]');
    if (!link) return;
    const chat = document.querySelector('shopify-chat');
    const button = chat && findChatButton(chat.shadowRoot);
    if (button) {
      event.preventDefault();
      // Defer so this click finishes first; Inbox treats it as an outside click and would close again.
      setTimeout(() => button.click(), 0);
    }
  });

  if (!customElements.get('cz-carousel')) {
    customElements.define('cz-carousel', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-cz-track]');
        this.prev = this.querySelector('[data-cz-prev]');
        this.next = this.querySelector('[data-cz-next]');
        if (!this.track) return;
        this.prev && this.prev.addEventListener('click', () => this.step(-1));
        this.next && this.next.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        new ResizeObserver(() => this.update()).observe(this.track);
        this.update();
      }

      step(direction) {
        const slide = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const width = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: direction * width, behavior: 'smooth' });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.toggleAttribute('data-scrollable', max > 4);
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max - 4;
      }
    });
  }

  if (!customElements.get('cz-bed-finder')) {
    customElements.define('cz-bed-finder', class extends HTMLElement {
      connectedCallback() {
        this.steps = [...this.querySelectorAll('[data-cz-step]')];
        this.results = this.querySelector('[data-cz-results]');
        this.progress = this.querySelector('[data-cz-progress]');
        this.answers = {};
        this.addEventListener('click', (event) => {
          const option = event.target.closest('[data-cz-option]');
          if (option) return this.choose(option);
          if (event.target.closest('[data-cz-back]')) return this.show(this.current - 1);
          if (event.target.closest('[data-cz-restart]')) {
            this.answers = {};
            this.querySelectorAll('[data-cz-option][aria-pressed="true"]').forEach((el) => el.setAttribute('aria-pressed', 'false'));
            return this.show(0);
          }
        });
        this.show(0);
      }

      show(index) {
        this.current = Math.max(0, index);
        this.steps.forEach((step, i) => { step.hidden = i !== this.current; });
        this.results.hidden = this.current < this.steps.length;
        if (this.progress) {
          const total = this.steps.length;
          this.progress.textContent = this.current < total ? `Step ${this.current + 1} of ${total}` : 'Your matches';
          this.style.setProperty('--cz-finder-progress', `${Math.min(this.current, total) / total * 100}%`);
        }
        if (this.current >= this.steps.length) this.renderResults();
      }

      choose(option) {
        const step = option.closest('[data-cz-step]');
        step.querySelectorAll('[data-cz-option]').forEach((el) => el.setAttribute('aria-pressed', String(el === option)));
        this.answers[step.dataset.czStep] = option.dataset;
        this.show(this.current + 1);
      }

      async loadBeds() {
        if (!this.bedsPromise) {
          const handle = this.dataset.collection || 'beds';
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          this.bedsPromise = fetch(`${root}collections/${handle}/products.json?limit=250`)
            .then((response) => response.json())
            .then((data) => data.products || []);
        }
        return this.bedsPromise;
      }

      async renderResults() {
        const list = this.results.querySelector('[data-cz-result-list]');
        const summary = this.results.querySelector('[data-cz-summary]');
        const viewAll = this.results.querySelector('[data-cz-view-all]');
        list.innerHTML = '<p class="cz-finder__loading">Finding your beds…</p>';

        const size = this.answers.size || {};
        const style = this.answers.style || {};
        const budget = this.answers.budget || {};
        const sizeTags = (size.tags || '').split('|').filter(Boolean);
        const styleTag = style.tag || '';
        const min = parseFloat(budget.min || 0);
        const max = budget.max ? parseFloat(budget.max) : Infinity;

        let beds = [];
        try {
          beds = await this.loadBeds();
        } catch (error) {
          list.innerHTML = '<p class="cz-finder__loading">Sorry, we couldn’t load beds just now. Please try again.</p>';
          return;
        }

        const tagsOf = (p) => (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(', '));
        const priceOf = (p) => Math.min(...p.variants.map((v) => parseFloat(v.price)));
        const fits = beds.filter((p) => {
          const tags = tagsOf(p);
          return (!sizeTags.length || sizeTags.some((t) => tags.includes(t))) && (!styleTag || tags.includes(styleTag));
        });
        let matches = fits.filter((p) => priceOf(p) >= min && priceOf(p) <= max);
        let note = '';
        if (!matches.length && fits.length) {
          matches = fits;
          note = ' None are in that budget, so here are the closest options.';
        }
        matches.sort((a, b) => priceOf(a) - priceOf(b));

        summary.textContent = matches.length
          ? `We found ${matches.length} ${size.label || ''} ${style.label || ''} bed${matches.length === 1 ? '' : 's'} for you.${note}`.replace(/\s+/g, ' ')
          : 'We couldn’t find an exact match. Try a different style or size, or browse all beds.';

        const money = (value) => '£' + value.toLocaleString('en-GB', { minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 });
        const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        list.innerHTML = matches.slice(0, 3).map((p) => {
          const image = p.images && p.images[0] ? p.images[0].src : '';
          const imageUrl = image ? image + (image.includes('?') ? '&' : '?') + 'width=600' : '';
          const varies = new Set(p.variants.map((v) => v.price)).size > 1;
          return `<a class="cz-finder__result" href="/products/${p.handle}">
            <span class="cz-finder__result-media">${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(p.title)}" loading="lazy" width="600" height="450">` : ''}</span>
            <span class="cz-finder__result-body">
              <span class="cz-finder__result-title">${escapeHtml(p.title)}</span>
              <span class="cz-finder__result-price">${varies ? 'From ' : ''}${money(priceOf(p))}</span>
            </span>
          </a>`;
        }).join('');

        const handle = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const filterTags = [sizeTags[0], styleTag].filter(Boolean).map(handle).join('+');
        viewAll.href = `/collections/${this.dataset.collection || 'beds'}${filterTags ? '/' + filterTags : ''}`;
        viewAll.hidden = !matches.length;
      }
    });
  }
})();
